import { useState, useEffect, useCallback } from 'react';
import { Article } from '../types';
import {
  getAllArticles,
  saveArticle,
  deleteArticle as deleteArticleDB,
  initDefaultArticle,
} from '../db/indexedDB';

export function useArticles() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    await initDefaultArticle();
    const data = await getAllArticles();
    const sorted = data.sort((a, b) => b.updatedAt - a.updatedAt);
    setArticles(sorted);
    if (sorted.length > 0 && !selectedArticle) {
      setSelectedArticle(sorted[0]);
    }
    setLoading(false);
  }, [selectedArticle]);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const addArticle = useCallback(async (title: string, content: string) => {
    const now = Date.now();
    const article: Article = {
      id: `article-${now}`,
      title: title.trim(),
      content: content.trim(),
      createdAt: now,
      updatedAt: now,
    };
    await saveArticle(article);
    setArticles((prev) => [article, ...prev]);
    setSelectedArticle(article);
    return article;
  }, []);

  const updateArticle = useCallback(async (id: string, title: string, content: string) => {
    const existing = articles.find((a) => a.id === id);
    if (!existing) return;
    const article: Article = {
      ...existing,
      title: title.trim(),
      content: content.trim(),
      updatedAt: Date.now(),
    };
    await saveArticle(article);
    setArticles((prev) =>
      prev.map((a) => (a.id === id ? article : a)).sort((a, b) => b.updatedAt - a.updatedAt)
    );
    if (selectedArticle?.id === id) {
      setSelectedArticle(article);
    }
    return article;
  }, [articles, selectedArticle]);

  const deleteArticle = useCallback(async (id: string) => {
    await deleteArticleDB(id);
    setArticles((prev) => prev.filter((a) => a.id !== id));
    if (selectedArticle?.id === id) {
      const remaining = articles.filter((a) => a.id !== id);
      setSelectedArticle(remaining.length > 0 ? remaining[0] : null);
    }
  }, [articles, selectedArticle]);

  const selectArticle = useCallback((article: Article) => {
    setSelectedArticle(article);
  }, []);

  return {
    articles,
    loading,
    selectedArticle,
    addArticle,
    updateArticle,
    deleteArticle,
    selectArticle,
    refresh: loadArticles,
  };
}
